import React, { useState, useEffect } from 'react';
import { loadSchemaData, getTableStats } from '../data/mockData';
import SchemaTable from './SchemaTable';
import SchemaOverview from './SchemaOverview';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const Dashboard = ({ selectedTable }) => {
  const [schemaData, setSchemaData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        // Try to load from API first (set to true to use live data)
        // Set to false to use CSV file
        const USE_API = true; // Change this to toggle between API and CSV
        const data = await loadSchemaData(USE_API);
        setSchemaData(data);
        
        // Get the actual cache generation timestamp
        try {
          const response = await fetch(`${API_URL}/api/cache/info`);
          const cacheInfo = await response.json();
          if (cacheInfo.generated_at) {
            // Format the timestamp nicely
            const date = new Date(cacheInfo.generated_at);
            setLastUpdated(date.toLocaleString());
          }
        } catch (e) {
          console.log('Could not fetch cache timestamp');
        }
      } catch (error) {
        console.error('Failed to load schema data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, []);

  if (loading) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="flex flex-col items-center justify-center h-64 space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-primary-600"></div>
          <p className="text-marble-400 text-sm animate-pulse">Loading schema data...</p>
        </div>
      </div>
    );
  }

  const tableStats = getTableStats(selectedTable, schemaData);
  const tableColumns = tableStats.columns || [];

  const handleDownloadCSV = async () => {
    // Download CSV for current table - force download using fetch + Blob
    try {
      const url = `${API_URL}/api/export/schema-csv?table_name=${selectedTable}`;
      const response = await fetch(url);
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `${selectedTable}_schema.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Download failed:', error);
      alert('Failed to download CSV. Please try again.');
    }
  };

  const handleDownloadAllCSV = async () => {
    // Download CSV for all tables (like R script output)
    try {
      const url = `${API_URL}/api/export/schema-csv`;
      const response = await fetch(url);
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = 'schema_summary.csv';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Download failed:', error);
      alert('Failed to download CSV. Please try again.');
    }
  };

  const handleRefreshCache = async () => {
    const confirmRefresh = window.confirm(
      'Refresh schema data from database? This will take 1-2 minutes and query all tables.'
    );
    
    if (!confirmRefresh) return;
    
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/refresh-cache`, {
        method: 'POST'
      });
      const result = await response.json();
      
      if (result.status === 'success') {
        alert(`✅ Cache refreshed! ${result.total_records} schema records updated.`);
        // Reload the page to show new data
        window.location.reload();
      } else {
        alert('❌ Failed to refresh cache. Check console for details.');
      }
    } catch (error) {
      console.error('Refresh failed:', error);
      alert('❌ Failed to refresh cache. Please try again.');
      setLoading(false);
    }
  };

  if (!schemaData || schemaData.length === 0) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="text-center">
          <p className="text-marble-400">No schema data available. Please ensure schema_summary.csv is in the public directory.</p>
        </div>
      </div>
    );
  }

  if (tableColumns.length === 0) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="text-center">
          <p className="text-marble-400">No data found for table: {selectedTable}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-7xl mx-auto">
      {/* Page header with download buttons */}
      <div className="mb-8">
        <div className="flex items-start justify-between pb-4">
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-gray-800 dark:text-primary-200 uppercase tracking-wider transition-colors">
              {selectedTable.replace(/_/g, ' ')}
            </h1>
            <p className="mt-2 text-sm text-gray-600 dark:text-marble-400 transition-colors">
              Schema definition and data quality metrics
            </p>
            {lastUpdated && (
              <p className="mt-2 text-xs text-gray-500 dark:text-marble-500 flex items-center space-x-2 transition-colors">
                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                </svg>
                <span>Last updated: {lastUpdated}</span>
              </p>
            )}
          </div>
          
          {/* Download buttons */}
          <div className="flex space-x-3 flex-shrink-0">
            <button
              onClick={handleDownloadCSV}
              className="btn-secondary flex items-center space-x-2"
              title="Download schema for this table as CSV"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              <span>Export Table</span>
            </button>
            
            <button
              onClick={handleDownloadAllCSV}
              className="btn-primary flex items-center space-x-2"
              title="Download schema for all tables (schema_summary.csv)"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <span>Export All</span>
            </button>
            
            <button
              onClick={handleRefreshCache}
              disabled={loading}
              className="btn-success flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Refresh schema data from database (takes 1-2 minutes)"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Refreshing...</span>
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  <span>Refresh Data</span>
                </>
              )}
            </button>
          </div>
        </div>
        <div className="border-b-2 border-primary-600/50 mt-4"></div>
      </div>

      {/* Schema Overview Cards */}
      <SchemaOverview stats={tableStats} tableName={selectedTable} />

      {/* Schema Data Table */}
      <div className="mt-8">
        <SchemaTable columns={tableColumns} tableName={selectedTable} />
      </div>
    </div>
  );
};

export default Dashboard;